import { Component, HostBinding, Input, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { RefreshService } from '../../core/refresh.service';
import { MaterialModule } from '../../material.module';
import { UserLocation } from '../../models/user/location.model';
import { AnalyticsService } from '../../core/analytics';
import * as fromRoot from '../../reducers';
import { PaginationComponent } from '../pagination/pagination.component';
import { buildUrlForLocationChange } from '../navbar/location-utils';
import { LocationPickerBase } from '../navbar/location-picker.base';

export type LocationSwitcherVariant = 'desktop' | 'mobile' | 'legacy';

@Component({
  selector: 'location-switcher',
  templateUrl: './location-switcher.component.html',
  styleUrls: ['./location-switcher.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, MaterialModule, PaginationComponent],
})
export class LocationSwitcherComponent extends LocationPickerBase {
  @Input() public variant: LocationSwitcherVariant = 'desktop';
  @ViewChild('locationMenu') public locationMenu: MatSelect;

  @HostBinding('class.location-switcher-host')
  public readonly hostClass = true;

  @HostBinding('class.location-switcher-host--mobile')
  public get isMobileHost(): boolean {
    return this.variant === 'mobile';
  }

  public mobileInlinePickerOpen = false;

  constructor(
    private readonly analytics: AnalyticsService,
    private readonly refreshService: RefreshService,
    store: Store<fromRoot.State>,
  ) {
    super(store);
  }

  public get panelClass(): string {
    if (this.variant === 'legacy') {
      return 'ngx-navbar__panel';
    }
    const mobileClass = this.variant === 'mobile' ? ' ngx-navbar__panel--next-gen-mobile' : '';
    return `ngx-navbar__panel ngx-navbar__panel--next-gen${mobileClass}`;
  }

  public get panelWidth(): string | null {
    if (this.variant === 'desktop') {
      return '265px';
    }
    return this.variant === 'mobile' ? '100%' : null;
  }

  public updateLocation(location: UserLocation): void {
    const matched = this.locations?.find(
      (loc) => loc.locationNumber === location.locationNumber,
    ) ?? location;
    this.selectedLocation = location;
    this.analytics.sendEvent('Navbar Location Change', {
      'Location #': matched.locationNumber,
      'Location Name': matched.locationName,
    });
    this.refreshService.setRefreshReason('location-change');
    globalThis.location.href = buildUrlForLocationChange(String(location.locationNumber));
  }

  public stateDisabled(): boolean {
    return Array.isArray(this.locations) && this.locations.length <= 1;
  }

  public get locationsLoaded(): boolean {
    return Array.isArray(this.locations);
  }

  public toggleMobileInlinePicker(): void {
    if (this.stateDisabled()) {
      return;
    }
    this.mobileInlinePickerOpen = !this.mobileInlinePickerOpen;
    if (!this.mobileInlinePickerOpen) {
      this.clearLocationFiltering();
    }
  }

  public selectMobileLocation(location: UserLocation): void {
    this.updateLocation(location);
    this.mobileInlinePickerOpen = false;
  }

  public openLocationMenu(event: Event): void {
    if (this.stateDisabled()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.locationMenu?.open();
  }

  public override clearLocationFiltering(): void {
    super.clearLocationFiltering();
    this.mobileInlinePickerOpen = false;
  }

  public override toggleFavorite(location: UserLocation): void {
    const isCurrentlyFavorite = this.favoriteLocationNumbers.includes(location.locationNumber);
    this.analytics.sendEvent(
      isCurrentlyFavorite ? 'Unfavorite Location' : 'Favorite Location',
      {
        'Location #': location.locationNumber,
        'Location Name': location.locationName
      }
    );
    super.toggleFavorite(location);
  }
}
